/**
 * NOS-219: dynamic latest-posts block.
 *
 * Renders the org's most recent published blog posts live from `blog_posts`
 * (RLS allows public read of published rows), so the band never freezes at
 * import time — new posts appear without editing the section. Content carries
 * settings only: heading, count, show_images, view_all placement + label.
 *
 * Data fetch: one org-scoped query fired on mount via the shared supabase
 * client — the same client and filters the public BlogListingPage uses — so
 * the block loads in parallel with the rest of the page (no waterfall).
 *
 * View All is part of the block (header-right or below the grid, configurable)
 * linking to the blog index — no separate stacked button section.
 */
import { useEffect, useState } from "react"
import { FileText } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { HEADING_COLOR, type BlockComponentProps } from "./shared"

export interface LatestPostRow {
  id: string
  title: string
  slug: string
  excerpt: string | null
  cover_image: string | null
  published_at: string | null
}

const DEFAULT_COUNT = 3
const MAX_COUNT = 12

export function clampPostCount(raw: unknown): number {
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1) return DEFAULT_COUNT
  return Math.min(MAX_COUNT, Math.trunc(n))
}

/**
 * Blog index path for the current host: `/blog` on org subdomains and custom
 * domains; `/{orgSlug}/blog` when the site is served path-based on the app
 * domain (mirrors how App.tsx mounts BlogListingPage).
 */
export function blogBasePath(orgSlug: string | null | undefined): string {
  if (orgSlug && typeof window !== "undefined") {
    const p = window.location.pathname
    if (p === `/${orgSlug}` || p.startsWith(`/${orgSlug}/`)) return `/${orgSlug}/blog`
  }
  return "/blog"
}

function formatPostDate(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export function LatestPostsBlock({ content, org, inBuilder, resolved }: BlockComponentProps) {
  const heading = (content?.heading as string) ?? "Latest Posts"
  const count = clampPostCount(content?.count)
  const showImages = (content?.show_images as boolean | undefined) ?? true
  const viewAllRaw = content?.view_all
  const viewAll: "header" | "below" | "none" =
    viewAllRaw === "below" || viewAllRaw === "none" ? viewAllRaw : "header"
  const viewAllText = ((content?.view_all_text as string) || "View All").trim() || "View All"
  const orgId = org?.id ?? null

  // null = loading; [] = loaded, no posts
  const [posts, setPosts] = useState<LatestPostRow[] | null>(null)

  useEffect(() => {
    if (!orgId) {
      setPosts([])
      return
    }
    let cancelled = false
    async function load() {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, cover_image, published_at")
        .eq("org_id", orgId as string)
        .eq("is_published", true)
        .not("published_at", "is", null)
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .limit(count)
      if (cancelled) return
      setPosts(error ? [] : ((data ?? []) as LatestPostRow[]))
    }
    load()
    return () => {
      cancelled = true
    }
  }, [orgId, count])

  const primaryColor = "var(--nos-color-primary, #3b82f6)"
  const sectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8"

  const blogHref = blogBasePath(org?.slug)
  // In the builder canvas, links must not navigate away — an <a> without href is inert.
  const linkHref = (href: string) => (inBuilder ? undefined : href)

  const loading = posts === null
  const empty = posts !== null && posts.length === 0

  // Public render: an empty band would be dead weight on a live site — hide it.
  // Builder canvas keeps a placeholder so the section stays visible/editable.
  if (empty && !inBuilder) return null

  const viewAllLink = (extraClass: string) => (
    <a
      href={linkHref(blogHref)}
      className={`text-sm font-medium whitespace-nowrap ${extraClass}`.trim()}
      style={{ color: primaryColor }}
    >
      {viewAllText} →
    </a>
  )

  return (
    <section className={sectionClass} style={resolved.sectionStyle} data-testid="latest-posts">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-end justify-between gap-4">
          <h2 className="text-2xl font-bold" style={{ color: HEADING_COLOR }}>
            {heading}
          </h2>
          {viewAll === "header" && !empty && viewAllLink("")}
        </div>

        {loading ? (
          <div className="grid gap-6 @sm:grid-cols-2 @lg:grid-cols-3">
            {Array.from({ length: Math.min(count, 3) }, (_, i) => (
              <div key={i} className="animate-pulse overflow-hidden rounded-xl border bg-card" data-testid="latest-posts-skeleton">
                {showImages && <div className="aspect-video bg-muted" />}
                <div className="space-y-2 p-4">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-1/2 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed py-10 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">No published posts yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Recent blog posts will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 @sm:grid-cols-2 @lg:grid-cols-3">
            {posts.map((post) => (
              <a
                key={post.id}
                href={linkHref(`${blogHref}/${post.slug}`)}
                className="group block overflow-hidden rounded-xl border bg-card transition hover:shadow-lg"
              >
                {showImages && (
                  <div className="aspect-video overflow-hidden bg-muted">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt=""
                        className="h-full w-full object-cover transition group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="flex h-full items-center justify-center"
                        style={{ backgroundColor: "color-mix(in srgb, var(--nos-color-primary, #3b82f6) 12%, transparent)" }}
                      >
                        <FileText className="h-10 w-10 opacity-50" style={{ color: primaryColor }} />
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold group-hover:underline">{post.title}</h3>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {formatPostDate(post.published_at)}
                    {org?.name ? ` · ${org.name}` : ""}
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}

        {viewAll === "below" && !empty && !loading && (
          <div className="mt-8 text-center">{viewAllLink("")}</div>
        )}
      </div>
    </section>
  )
}
